"use client";

import { type CSSProperties, type FC, useEffect, useState } from "react";
import { cn } from "@/lib/utils";

// Deterministic 5-hue seed from an arbitrary id (FNV-1a + xorshift).
const fnv1a32 = (input: string): number => {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
};

const nextHue = (seed: number): { hue: number; next: number } => {
  let x = seed;
  x ^= x << 13;
  x ^= x >>> 17;
  x ^= x << 5;
  return { hue: x % 360, next: x >>> 0 };
};

const agentHues = (
  agentId: string,
): [number, number, number, number, number] => {
  let s = fnv1a32(agentId);
  const out: number[] = [];
  for (let i = 0; i < 5; i++) {
    const step = nextHue(s);
    out.push(step.hue);
    s = step.next;
  }
  return out as [number, number, number, number, number];
};

const buildGradient = (
  hues: [number, number, number, number, number],
): string => {
  const [h1, h2, h3, h4, h5] = hues;
  return [
    `radial-gradient(circle at 24% 26%, hsl(${h1} 100% 60%) 0%, transparent 58%)`,
    `radial-gradient(circle at 78% 28%, hsl(${h2} 98% 58%) 0%, transparent 54%)`,
    `radial-gradient(circle at 46% 84%, hsl(${h3} 96% 56%) 0%, transparent 50%)`,
    `radial-gradient(circle at 90% 68%, hsl(${h4} 100% 54%) 0%, transparent 44%)`,
    `hsl(${h5} 72% 46%)`,
  ].join(", ");
};

const cssIdent = (s: string): string =>
  s.replace(/[^a-z0-9]/gi, "-").toLowerCase();

export type AgentOrbState =
  | "idle"
  | "listening"
  | "thinking"
  | "speaking"
  | "asleep";

export type AgentOrbProps = {
  agentId: string;
  /** Drives the animation language. */
  state?: AgentOrbState;
  /** Outer diameter in px. Default 200. */
  size?: number;
  /**
   * Live audio level (0–1). When set, the orb's breathing, halo, and core
   * track the audio instead of the ambient keyframes. Pair with
   * `useAudioLevel` to drive it from a MediaStream or <audio> element.
   */
  audioLevel?: number;
  /** Custom aria-label. Defaults to "Agent <state>". */
  label?: string;
  className?: string;
};

// createMediaElementSource can only be called once per element — cache it.
const elementSources = new WeakMap<
  HTMLMediaElement,
  { context: AudioContext; node: MediaElementAudioSourceNode }
>();

/**
 * Real-time audio level (0–1) from a MediaStream (e.g. microphone) or an
 * HTMLMediaElement (e.g. TTS playback). Fast attack, slow release — tuned
 * to feel like speech when fed into `<AgentOrb audioLevel={level} />`.
 */
export const useAudioLevel = (
  source?: MediaStream | HTMLMediaElement | null,
): number => {
  const [level, setLevel] = useState(0);

  useEffect(() => {
    if (!source) {
      setLevel(0);
      return;
    }

    let context: AudioContext;
    let node: AudioNode;
    let ownsContext = false;

    if (source instanceof MediaStream) {
      context = new AudioContext();
      ownsContext = true;
      node = context.createMediaStreamSource(source);
    } else {
      let entry = elementSources.get(source);
      if (!entry) {
        const ctx = new AudioContext();
        const n = ctx.createMediaElementSource(source);
        n.connect(ctx.destination);
        entry = { context: ctx, node: n };
        elementSources.set(source, entry);
      }
      context = entry.context;
      node = entry.node;
    }

    // Browsers may start (or leave) the context suspended outside a user
    // gesture — without resume() the analyser reads silence forever.
    if (context.state === "suspended") {
      void context.resume();
    }

    const analyser = context.createAnalyser();
    analyser.fftSize = 256;
    node.connect(analyser);

    const data = new Uint8Array(analyser.fftSize);
    let raf = 0;
    let smoothed = 0;

    const tick = () => {
      analyser.getByteTimeDomainData(data);
      let sum = 0;
      for (let i = 0; i < data.length; i++) {
        const v = (data[i] - 128) / 128;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / data.length);
      const target = Math.min(1, rms * 4);
      smoothed = target > smoothed ? target : smoothed * 0.85;
      setLevel(Math.round(smoothed * 100) / 100);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      node.disconnect(analyser);
      analyser.disconnect();
      if (ownsContext) {
        void context.close();
      }
      setLevel(0);
    };
  }, [source]);

  return level;
};

/**
 * CSS-only animated agent orb. Five distinct motion personalities
 * keyed off `state`, all running on the compositor thread, smoothly
 * transitioning saturation / brightness / opacity between states.
 *
 * - **idle** — slow breath, slow rotation, ambient halo
 * - **listening** — sonar ripples + faster pulse + boosted saturation
 * - **thinking** — sweeping arc + fast inner swirl + brighter shimmer
 * - **speaking** — rapid breath + bright core + extra saturation
 * - **asleep** — desaturated, dim, very slow drift
 *
 * No WebGL, no Canvas, no Rive — pure CSS layers + data-attribute
 * driven CSS variables. Renders identically in dev and prod.
 */
export const AgentOrb: FC<AgentOrbProps> = ({
  agentId,
  state = "idle",
  size = 200,
  audioLevel,
  label,
  className,
}) => {
  const hues = agentHues(agentId);
  const background = buildGradient(hues);
  // Single accent color for sonar rings — pick the brightest seeded hue.
  const accent = `hsl(${hues[0]} 100% 70%)`;
  const id = `agent-orb-${cssIdent(agentId)}`;
  const audioDriven = audioLevel != null;
  const clampedLevel = audioDriven
    ? Math.min(1, Math.max(0, audioLevel))
    : undefined;

  return (
    <div
      role="img"
      aria-label={label ?? `Agent ${state}`}
      data-state={state}
      data-audio={audioDriven ? "" : undefined}
      className={cn(
        "relative inline-flex shrink-0 items-center justify-center",
        id,
        className,
      )}
      style={
        {
          height: size,
          width: size,
          "--audio-level": clampedLevel,
        } as CSSProperties
      }
    >
      {/* Sonar rings — two staggered ripples. CSS gates visibility per state. */}
      <div
        aria-hidden
        className={`pointer-events-none absolute inset-0 rounded-full border-2 ${id}-sonar-1`}
        style={{ borderColor: accent }}
      />
      <div
        aria-hidden
        className={`pointer-events-none absolute inset-0 rounded-full border-2 ${id}-sonar-2`}
        style={{ borderColor: accent }}
      />

      {/* Outer halo — blurred gradient bleed, pulses scale + opacity. */}
      <div
        aria-hidden
        className={`pointer-events-none absolute inset-0 rounded-full blur-2xl ${id}-halo`}
        style={{ background }}
      />

      {/* Main orb body — breathes (scale), filter-modulated (saturate/brightness). */}
      <div
        className={`relative isolate flex h-full w-full items-center justify-center overflow-hidden rounded-full shadow-xl ring-2 ring-white/40 dark:ring-white/15 ${id}-orb`}
      >
        {/* Rotating gradient swirl — color blobs sweep around. */}
        <div
          aria-hidden
          className={`absolute -inset-[10%] rounded-full ${id}-spin`}
          style={{ background }}
        />

        {/* Counter-rotating shimmer — breaks the carousel feel, adds depth. */}
        <div
          aria-hidden
          className={`absolute inset-[5%] rounded-full ${id}-shimmer`}
          style={{
            background:
              "conic-gradient(from 0deg, transparent 0%, rgba(255,255,255,0.25) 35%, transparent 60%, rgba(255,255,255,0.15) 85%, transparent 100%)",
            mixBlendMode: "soft-light",
          }}
        />

        {/* Thinking sweep arc — narrow bright wedge orbiting the orb. */}
        <div
          aria-hidden
          className={`absolute inset-0 ${id}-sweep`}
          style={{
            background:
              "conic-gradient(from 0deg, transparent 0%, rgba(255,255,255,0.75) 8%, transparent 18%)",
            mixBlendMode: "overlay",
          }}
        />

        {/* Specular highlight — fixed bright spot, sells the "sphere" read. */}
        <div
          aria-hidden
          className="absolute top-[8%] left-[18%] h-[28%] w-[40%] rounded-full bg-white/35 blur-xl"
        />

        {/* Inner core — bright center, pulses brighter when speaking. */}
        <div
          aria-hidden
          className={`absolute inset-[35%] rounded-full ${id}-core`}
          style={{
            background:
              "radial-gradient(circle, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0) 70%)",
          }}
        />
      </div>

      <style>{`
        /* ── Per-state CSS variables ────────────────────────────────── */
        .${id} {
          --halo-amp:        0.06;
          --halo-period:     5s;
          --halo-opacity:    0.55;
          --orb-amp:         0.02;
          --orb-period:      4s;
          --spin-period:    45s;
          --shimmer-period: 25s;
          --sweep-period:    1.4s;
          --saturate:        1;
          --brightness:      1;
          --core-opacity:    0.7;
          --overall-opacity: 1;
          --sonar-display:   none;
          --sweep-display:   none;

          opacity: var(--overall-opacity);
          transition: opacity 0.6s ease;
        }

        .${id}[data-state="listening"] {
          --halo-amp:        0.15;
          --halo-period:     2s;
          --halo-opacity:    0.7;
          --orb-amp:         0.04;
          --orb-period:      2s;
          --spin-period:    25s;
          --shimmer-period: 18s;
          --saturate:        1.15;
          --sonar-display:   block;
        }

        .${id}[data-state="thinking"] {
          --halo-period:     3s;
          --orb-period:      3s;
          --spin-period:    12s;
          --shimmer-period:  8s;
          --saturate:        1.05;
          --sweep-display:   block;
        }

        .${id}[data-state="speaking"] {
          --halo-amp:        0.20;
          --halo-period:     0.8s;
          --halo-opacity:    0.8;
          --orb-amp:         0.06;
          --orb-period:      0.8s;
          --spin-period:    30s;
          --shimmer-period: 15s;
          --saturate:        1.2;
          --brightness:      1.1;
          --core-opacity:    1;
        }

        .${id}[data-state="asleep"] {
          --halo-amp:        0.02;
          --halo-period:     8s;
          --halo-opacity:    0.3;
          --orb-amp:         0.005;
          --orb-period:      8s;
          --spin-period:    90s;
          --shimmer-period: 60s;
          --saturate:        0.4;
          --brightness:      0.65;
          --overall-opacity: 0.6;
          --core-opacity:    0.3;
        }

        /* ── Keyframes ─────────────────────────────────────────────── */
        @keyframes ${id}-halo-kf {
          0%, 100% { opacity: calc(var(--halo-opacity) * 0.85); transform: scale(1); }
          50%      { opacity: var(--halo-opacity);              transform: scale(calc(1 + var(--halo-amp))); }
        }
        @keyframes ${id}-orb-kf {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(calc(1 + var(--orb-amp))); }
        }
        @keyframes ${id}-spin-kf {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        @keyframes ${id}-shimmer-kf {
          from { transform: rotate(360deg); }
          to   { transform: rotate(0deg); }
        }
        @keyframes ${id}-sweep-kf {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        @keyframes ${id}-core-kf {
          0%, 100% { opacity: calc(var(--core-opacity) * 0.7); }
          50%      { opacity: var(--core-opacity); }
        }
        @keyframes ${id}-sonar-kf {
          0%   { opacity: 0.7; transform: scale(1); }
          100% { opacity: 0;   transform: scale(1.8); }
        }

        /* ── Layer animation bindings ──────────────────────────────── */
        .${id}-halo {
          animation: ${id}-halo-kf var(--halo-period) ease-in-out infinite;
        }
        .${id}-orb {
          animation: ${id}-orb-kf var(--orb-period) ease-in-out infinite;
          filter:    saturate(var(--saturate)) brightness(var(--brightness));
          transition: filter 0.6s ease;
        }
        .${id}-spin {
          animation: ${id}-spin-kf var(--spin-period) linear infinite;
        }
        .${id}-shimmer {
          animation: ${id}-shimmer-kf var(--shimmer-period) linear infinite;
        }
        .${id}-sweep {
          display:   var(--sweep-display);
          animation: ${id}-sweep-kf var(--sweep-period) linear infinite;
        }
        .${id}-core {
          animation: ${id}-core-kf var(--orb-period) ease-in-out infinite;
          filter:    blur(8px);
        }
        .${id}-sonar-1,
        .${id}-sonar-2 {
          display:   var(--sonar-display);
          animation: ${id}-sonar-kf 2s ease-out infinite;
        }
        .${id}-sonar-2 {
          animation-delay: 1s;
        }

        /* ── Audio-driven mode — level replaces the ambient breathing ── */
        .${id}[data-audio] .${id}-orb {
          animation: none;
          transform: scale(calc(1 + var(--audio-level, 0) * 0.1));
          transition: filter 0.6s ease, transform 90ms ease-out;
        }
        .${id}[data-audio] .${id}-halo {
          animation: none;
          opacity: calc(var(--halo-opacity) * 0.7 + var(--audio-level, 0) * 0.45);
          transform: scale(calc(1 + var(--audio-level, 0) * 0.3));
          transition: opacity 90ms ease-out, transform 90ms ease-out;
        }
        .${id}[data-audio] .${id}-core {
          animation: none;
          opacity: calc(var(--core-opacity) * 0.5 + var(--audio-level, 0) * 0.6);
          transition: opacity 90ms ease-out;
        }

        @media (prefers-reduced-motion: reduce) {
          .${id}-halo, .${id}-orb, .${id}-spin, .${id}-shimmer,
          .${id}-sweep, .${id}-core, .${id}-sonar-1, .${id}-sonar-2 {
            animation: none;
          }
          .${id}[data-audio] .${id}-orb,
          .${id}[data-audio] .${id}-halo {
            transform: none;
          }
        }
      `}</style>
    </div>
  );
};

AgentOrb.displayName = "AgentOrb";
